/**
 * Creation-draft helpers, shared by the Sites list and the Tests list.
 *
 * A creation-draft is an in-progress CreateFlow item: a user/{uid}/url/{id}
 * doc flagged { draft: true }. It is NEVER served publicly (api/site.js and
 * App.js both refuse to serve a draft), generates no analytics (it is never
 * served, so no pageview snippet is ever injected), and a test draft is not
 * plugin-claimable because its pluginUserTesting bridge doc is only written
 * when the flow finishes. The flag is cleared the moment the real item goes
 * live (the drop/figma finish, or the agent's deploy arriving on the doc).
 *
 * This is a brand-new field, deliberately NOT the legacy stringly `isDraft`
 * ("true" | "false" | "fromPlugin") used by the old Figmafolio portfolio
 * system, and deliberately NOT the nested `userTesting.status` ('active' |
 * 'draft') that means a test is paused / not accepting sessions. Those two
 * keep their existing meaning untouched.
 *
 * Convention so the two independent list trees can tell a draft's kind apart
 * using the same discriminators they already use for live items: a draft
 * SITE is a plain html (or figma + aeroSite) doc with no userTesting; a draft
 * TEST always carries a `userTesting` object. The CreateFlow honours this when
 * it writes the draft.
 */
import firebase from '../firebase';

/**
 * Discard a draft: remove its versions subcollection (batched, same shape as
 * AeroSites' delete), its pluginUserTesting bridge doc if one exists (figma
 * tests key the bridge by the url doc id), then the url doc itself. Blobs are
 * shared content-addressed storage and are left for a future GC, exactly like
 * the site delete path. Deleting a missing doc is a no-op, so the bridge
 * cleanup is safe for site drafts that never had one.
 */
export async function discardDraft({ uid, urlId }) {
  if (!uid || !urlId) throw new Error('discardDraft requires uid and urlId');
  const db = firebase.firestore();
  const urlRef = db.collection('user').doc(uid).collection('url').doc(urlId);

  const versionsSnap = await urlRef.collection('versions').get();
  const docs = versionsSnap.docs;
  for (let i = 0; i < docs.length; i += 400) {
    const batch = db.batch();
    docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  await db.collection('pluginUserTesting').doc(urlId).delete();
  await urlRef.delete();
}
